import { useEffect, useRef, useState } from 'react';

import { SparklesIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useFolioSessionCommands } from '@/hooks/use-folio-session';
import { useFolioToggle } from '@/hooks/use-folio-toggle';

import {
  clearGuidePulse,
  GUIDE_PULSE_CHANGE_EVENT,
  hasGuidePulseNeeded,
  useGuideStorageUserId,
} from './folio-guide-bootstrap';

export function FolioTriggerButton() {
  const userId = useGuideStorageUserId();
  const { isOpen, toggle } = useFolioToggle();
  const { pageContext } = useFolioSessionCommands();
  const [guideWaiting, setGuideWaiting] = useState(() => hasGuidePulseNeeded(pageContext, userId));

  useEffect(() => {
    function onPulseChange() {
      setGuideWaiting(hasGuidePulseNeeded(pageContext, userId));
    }
    window.addEventListener(GUIDE_PULSE_CHANGE_EVENT, onPulseChange);
    return () => {
      window.removeEventListener(GUIDE_PULSE_CHANGE_EVENT, onPulseChange);
    };
  }, [pageContext, userId]);

  // Clear pulse whenever the panel opens (via click, hotkey, or programmatic open).
  useEffect(() => {
    if (isOpen && guideWaiting) {
      clearGuidePulse(pageContext, userId);
    }
  }, [isOpen, guideWaiting, pageContext, userId]);

  // Don't surface Folio on unregistered routes (404, route errors) — the assistant
  // has no meaningful context to reason about on those surfaces.
  if (pageContext.page === 'unregistered-route') {
    return null;
  }

  return <FolioTriggerButtonView isOpen={isOpen} guideWaiting={guideWaiting} onToggle={toggle} />;
}

export interface FolioTriggerButtonViewProps {
  /** Whether the Sean panel is open; the button then reads as pressed and offers to hide it. */
  readonly isOpen: boolean;
  /** Whether a guide is waiting; the closed button carries a dot and says so in its name until the panel opens. */
  readonly guideWaiting: boolean;
  /** Called on activation with the time the press began, which decides it (see `FolioToggleContextValue.toggle`). */
  readonly onToggle: (pressedAt: number) => void;
}

/** The Sean button in the app header, drawn from the panel's state; `FolioTriggerButton` binds it to the Sean session. */
export function FolioTriggerButtonView({ isOpen, guideWaiting, onToggle }: FolioTriggerButtonViewProps) {
  const showGuideCue = guideWaiting && !isOpen;
  // A click lands when the button is released (Space, too, activates on
  // release), but the user chose when it went down; the panel can change in
  // between. Pointer and keyboard presses are kept apart, so a press that
  // never became a click cannot stand in for the other kind.
  const pointerDownAtRef = useRef<number | null>(null);
  const keyDownAtRef = useRef<number | null>(null);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant={isOpen ? 'secondary' : 'outline'}
          size="sm"
          data-folio-trigger
          className="relative h-11 rounded-full px-3 shadow-xs transition duration-150 ease-out-quart"
          aria-label={isOpen ? 'Hide Sean panel' : showGuideCue ? 'Show Sean panel, guide waiting' : 'Show Sean panel'}
          aria-controls="folio-panel"
          aria-expanded={isOpen}
          onPointerDown={(event) => {
            pointerDownAtRef.current = event.timeStamp;
          }}
          onKeyDown={(event) => {
            if ((event.key === 'Enter' || event.key === ' ') && !event.repeat) {
              keyDownAtRef.current = event.timeStamp;
            }
          }}
          onBlur={() => {
            keyDownAtRef.current = null;
          }}
          onClick={(event) => {
            // A pointer click (`detail` 1 or more) completes the press that
            // went down here; a keyboard click, the key press that went down
            // here; an assistive-technology click with neither is its own press.
            const pressedAt = event.detail > 0 ? pointerDownAtRef.current : keyDownAtRef.current;
            keyDownAtRef.current = null;
            onToggle(pressedAt ?? event.timeStamp);
          }}
        >
          <SparklesIcon data-icon="inline-start" />
          {/* Also yields in a narrow app header, so a page's controls keep their room. */}
          <span className="hidden sm:inline @max-[24rem]/header:hidden">Sean</span>
          {showGuideCue ? (
            <span
              aria-hidden="true"
              className="absolute top-1.5 right-1.5 size-2 rounded-full bg-accent ring-2 ring-background"
            />
          ) : null}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">Sean assistant · Ctrl+Shift+F</TooltipContent>
    </Tooltip>
  );
}
